import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { Button } from 'dibs-elements/exports/Button.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type SbSharedContentModuleBannerText_banner$key } from './__generated__/SbSharedContentModuleBannerText_banner.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './SbSharedContentModuleBannerText.scss';

const bannerFragment = graphql`
    fragment SbSharedContentModuleBannerText_banner on BrowseContentModuleBannerType {
        title
        description
        eyeBrowText
        ctaUrl
        ctaCopy
        ctaStyle
        textAlignment
        textColor
        backgroundColor
    }
`;

type Props = {
    banner: SbSharedContentModuleBannerText_banner$key | null | undefined;
    isStaticBanner: boolean;
    useH1Header: boolean;
};

export const SbSharedContentModuleBannerText: FC<Props> = ({
    banner: bannerRef,
    isStaticBanner,
    useH1Header,
}) => {
    const {
        textAlignment,
        textColor,
        eyeBrowText,
        title,
        description,
        ctaUrl,
        ctaStyle,
        ctaCopy,
        backgroundColor,
    } = useFragment(bannerFragment, bannerRef) || {};

    const { isMobile } = useServerVarsContext();
    const isWhiteText = (textColor === 'white' && !!backgroundColor) || isStaticBanner;

    if (!title || !description) {
        return null;
    }

    const headerClassName = classnames(styles.header, dibsCss.m0, dibsCss.mbSmall);

    return (
        <div
            className={classnames(
                styles.text,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                dibsCss.flex,
                dibsCss.flexCol,
                isStaticBanner && [dibsCss.zBump, dibsCss.wFull, dibsCss.hFull],
                isWhiteText && [styles.captionColor],
                (textAlignment === 'center' || isMobile) && [dibsCss.textCenter]
            )}
        >
            {eyeBrowText && (
                <p className={classnames(styles.eyeBrow, dibsCss.mbSmall)}>{eyeBrowText}</p>
            )}
            {useH1Header ? (
                <h1 className={headerClassName}>{title}</h1>
            ) : (
                <HeadingLevel>
                    {Heading => <Heading className={headerClassName}>{title}</Heading>}
                </HeadingLevel>
            )}
            <div
                className={classnames(styles.bodyText, dibsCss.sassyFontHeaderSizeSmall)}
                dangerouslySetInnerHTML={{ __html: description || '' }}
            />
            {ctaCopy && ctaUrl && (
                <Button
                    dataTn="hero-banner-cta"
                    className={classnames(styles.cta, styles.important, {
                        [styles.ctaOutline]: ctaStyle === 'outlined',
                        [styles.ctaColor]: isWhiteText,
                    })}
                    type="secondary"
                >
                    {ctaCopy}
                </Button>
            )}
        </div>
    );
};
